import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  Bot,
  Play,
  Square,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Shield,
  Zap,
  Eye,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  Send,
  ChevronDown,
  ChevronUp,
  FileCode,
  Terminal,
  Activity,
  Search,
  Filter,
  Trash2,
  MessageSquare,
  ArrowRight,
} from 'lucide-react';
import { notify } from '@core/notifications/notify';

// ============================================================================
// Types
// ============================================================================

interface Incident {
  id: string;
  status: string;
  error: {
    id: string;
    source: string;
    severity: string;
    category: string;
    message: string;
    stack_trace?: string;
    file_path?: string;
    line_number?: number;
    endpoint?: string;
    http_status?: number;
    error_count: number;
    first_seen: string;
    last_seen: string;
    fingerprint: string;
  };
  diagnosis?: {
    root_cause: string;
    explanation: string;
    affected_components: string[];
    impact_assessment: {
      user_facing: boolean;
      data_integrity: boolean;
      security: boolean;
      performance: boolean;
      scope: string;
    };
    confidence: number;
    is_user_error?: boolean;
    error_domain?: string;
    user_guidance?: string;
    admin_action?: string;
    database_checks?: string[];
  };
  fixes: {
    id: string;
    type: string;
    title: string;
    description: string;
    risk_level: string;
    risk_explanation: string;
    confidence: number;
    patches?: { file_path: string; diff_summary: string; unified_diff?: string }[];
    commands?: { command: string; description: string }[];
    manual_steps?: string[];
    requires_restart: boolean;
    estimated_downtime: number;
  }[];
  selected_fix_id?: string;
  approval?: {
    approved_by: number;
    approved_at: string;
    notes?: string;
  };
  execution?: {
    status: string;
    started_at: string;
    completed_at?: string;
    error_message?: string;
  };
  timeline: {
    timestamp: string;
    type: string;
    description: string;
    actor?: string;
  }[];
  created_at: string;
  updated_at: string;
  resolved_at?: string;
}

interface Dashboard {
  status: 'running' | 'stopped';
  stats: {
    total_incidents: number;
    active: number;
    awaiting_approval: number;
    resolved: number;
    reverted: number;
    escalated: number;
  };
  recent_incidents: Incident[];
  error_stats: {
    total_errors: number;
    active_incidents: number;
    by_severity: Record<string, number>;
    by_source: Record<string, number>;
  };
  active_monitoring: {
    incident_id: string;
    fix_id: string;
    started_at: string;
    check_count: number;
    elapsed_ms: number;
    remaining_ms: number;
  }[];
  monitoring?: {
    prometheus: { status: string; metrics: { requests_per_sec: number; error_rate_5xx: number; p95_latency: number; memory_mb: number; in_flight: number; db_connections: number } | null };
    elasticsearch: { status: string | { status: string; indices: number }; logs_today: number; errors_last_hour: number; error_breakdown?: { message: string; count: number }[]; errors_by_service?: { service: string; count: number }[] };
    grafana: { status: string; url: string };
    kibana: { status: string; url: string };
  };
}

// ============================================================================
// API Helpers
// ============================================================================

const apiHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '',
});

const api = {
  async get(path: string) {
    const res = await fetch(`/api/ai/troubleshoot${path}`, { headers: apiHeaders() });
    if (!res.ok) throw new Error((await res.json()).error || res.statusText);
    return res.json();
  },
  async post(path: string, body?: any) {
    const res = await fetch(`/api/ai/troubleshoot${path}`, {
      method: 'POST',
      headers: apiHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new Error((await res.json()).error || res.statusText);
    return res.json();
  },
};

// ============================================================================
// Helpers
// ============================================================================

const severityColor: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200',
};

const statusConfig: Record<string, { color: string; icon: React.ElementType; label: string }> = {
  detected: { color: 'bg-gray-100 text-gray-700', icon: AlertTriangle, label: 'Detected' },
  diagnosing: { color: 'bg-blue-100 text-blue-700', icon: Search, label: 'Diagnosing...' },
  diagnosed: { color: 'bg-indigo-100 text-indigo-700', icon: Eye, label: 'Diagnosed' },
  fix_ready: { color: 'bg-amber-100 text-amber-800', icon: Zap, label: 'Awaiting Approval' },
  approved: { color: 'bg-green-100 text-green-700', icon: ThumbsUp, label: 'Approved' },
  applying: { color: 'bg-cyan-100 text-cyan-700', icon: Terminal, label: 'Applying Fix...' },
  validating: { color: 'bg-purple-100 text-purple-700', icon: Activity, label: 'Validating...' },
  resolved: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Resolved' },
  reverting: { color: 'bg-red-100 text-red-700', icon: RotateCcw, label: 'Reverting...' },
  reverted: { color: 'bg-red-100 text-red-800', icon: RotateCcw, label: 'Reverted' },
  escalated: { color: 'bg-orange-100 text-orange-800', icon: AlertTriangle, label: 'Escalated' },
  dismissed: { color: 'bg-gray-100 text-gray-500', icon: Trash2, label: 'Dismissed' },
};

const riskColor: Record<string, string> = {
  minimal: 'text-green-600',
  low: 'text-blue-600',
  medium: 'text-yellow-600',
  high: 'text-orange-600',
  critical: 'text-red-600',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

// ============================================================================
// Component
// ============================================================================

const TroubleshootAgent: React.FC = () => {
  const { hasPermission } = useAuth();
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [submitErrorText, setSubmitErrorText] = useState('');
  const [showSubmitForm, setShowSubmitForm] = useState(false);

  // Load data
  const loadDashboard = useCallback(async () => {
    try {
      const data = await api.get('/dashboard');
      setDashboard(data);
    } catch (err: any) {
      console.error('Failed to load dashboard:', err);
    }
  }, []);

  const loadIncidents = useCallback(async () => {
    try {
      const statusParam = statusFilter === 'active'
        ? 'detected,diagnosing,diagnosed,fix_ready,approved,applying,validating'
        : statusFilter === 'resolved' ? 'resolved' : statusFilter === 'all' ? '' : statusFilter;
      const query = statusParam ? `?status=${statusParam}` : '';
      const data = await api.get(`/incidents${query}`);
      setIncidents(data.incidents || []);
    } catch (err: any) {
      console.error('Failed to load incidents:', err);
    }
  }, [statusFilter]);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([loadDashboard(), loadIncidents()]);
    setIsLoading(false);
  }, [loadDashboard, loadIncidents]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadAll, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadAll]);

  // Actions
  const handleStartAgent = async () => {
    try {
      await api.post('/start');
      notify.success('Troubleshoot agent started');
      loadDashboard();
    } catch (err: any) { notify.error(err.message); }
  };

  const handleStopAgent = async () => {
    try {
      await api.post('/stop');
      notify.success('Troubleshoot agent stopped');
      loadDashboard();
    } catch (err: any) { notify.error(err.message); }
  };

  const handleApproveFix = async (incidentId: string, fixId: string) => {
    try {
      await api.post(`/incidents/${incidentId}/approve`, { fixId });
      notify.success('Fix approved — applying now');
      loadAll();
    } catch (err: any) { notify.error(err.message); }
  };

  const handleRejectFix = async (incidentId: string) => {
    try {
      await api.post(`/incidents/${incidentId}/reject`, { reason: 'Rejected by admin' });
      notify.info('Fix rejected');
      loadAll();
    } catch (err: any) { notify.error(err.message); }
  };

  const handleDismiss = async (incidentId: string) => {
    try {
      await api.post(`/incidents/${incidentId}/dismiss`, { reason: 'Dismissed by admin' });
      notify.info('Incident dismissed');
      loadAll();
    } catch (err: any) { notify.error(err.message); }
  };

  const handleRediagnose = async (incidentId: string) => {
    try {
      await api.post(`/incidents/${incidentId}/rediagnose`);
      notify.info('Re-diagnosis started');
      loadAll();
    } catch (err: any) { notify.error(err.message); }
  };

  const handleSubmitError = async () => {
    if (!submitErrorText.trim()) return;
    try {
      await api.post('/submit-error', { description: submitErrorText });
      notify.success('Error submitted for investigation');
      setSubmitErrorText('');
      setShowSubmitForm(false);
      loadAll();
    } catch (err: any) { notify.error(err.message); }
  };

  if (!hasPermission('system.config')) {
    return <div className="p-8 text-center text-gray-500">Access denied. System admin permissions required.</div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-lg">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Troubleshoot Agent</h1>
            <p className="text-sm text-gray-500">AI-powered error detection, diagnosis, and auto-fix with rollback</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowSubmitForm(!showSubmitForm)} className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2" title="Manually describe a problem for the AI to investigate">
            <Send className="w-4 h-4" /> Report an Issue
          </button>
          <button onClick={() => setAutoRefresh(!autoRefresh)} className={`px-3 py-2 text-sm rounded-lg flex items-center gap-2 ${autoRefresh ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-white border border-gray-200 text-gray-600'}`} title={autoRefresh ? 'Dashboard is auto-refreshing every 15 seconds. Click to pause.' : 'Dashboard is paused. Click to enable live updates.'}>
            <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} /> {autoRefresh ? 'Live' : 'Paused'}
          </button>
          {dashboard?.status === 'running' ? (
            <button onClick={handleStopAgent} className="px-4 py-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 flex items-center gap-2" title="Stop the agent from scanning for new errors. Active incidents will remain visible.">
              <Square className="w-4 h-4" /> Stop Agent
            </button>
          ) : (
            <button onClick={handleStartAgent} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2" title="Start watching logs, health checks, and API failures for errors. The agent will automatically detect, diagnose, and propose fixes.">
              <Play className="w-4 h-4" /> Start Agent
            </button>
          )}
        </div>
      </div>

      {/* Submit Error Form */}
      {showSubmitForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="font-medium text-gray-900 mb-1">Report an Issue for AI Investigation</h3>
          <p className="text-xs text-gray-500 mb-3">Describe the problem you're experiencing. The AI will analyse the system logs, source code, and recent changes to diagnose the root cause and suggest a fix.</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={submitErrorText}
              onChange={e => setSubmitErrorText(e.target.value)}
              placeholder="e.g. Users are getting 500 errors when trying to upload documents..."
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              onKeyDown={e => e.key === 'Enter' && handleSubmitError()}
            />
            <button onClick={handleSubmitError} className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700">
              Investigate
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard label="Agent Status" value={dashboard.status === 'running' ? 'Monitoring' : 'Stopped'} icon={Bot} color={dashboard.status === 'running' ? 'green' : 'gray'} tooltip={dashboard.status === 'running' ? 'Agent is scanning logs and health checks for errors' : 'Agent is not monitoring. Click Start Agent to begin.'} />
          <StatCard label="Active Incidents" value={dashboard.stats.active} icon={AlertTriangle} color="amber" tooltip="Errors currently being diagnosed or awaiting action" />
          <StatCard label="Awaiting Approval" value={dashboard.stats.awaiting_approval} icon={Clock} color="orange" highlight={dashboard.stats.awaiting_approval > 0} tooltip="Fixes ready for you to review and approve or reject" />
          <StatCard label="Resolved" value={dashboard.stats.resolved} icon={CheckCircle2} color="green" tooltip="Errors that were successfully fixed and confirmed stable" />
          <StatCard label="Reverted" value={dashboard.stats.reverted} icon={RotateCcw} color="red" tooltip="Fixes that were applied but caused issues, so the system was automatically rolled back" />
          <StatCard label="Escalated" value={dashboard.stats.escalated} icon={Shield} color="purple" tooltip="Issues the AI could not fix automatically and need manual investigation" />
        </div>
      )}

      {/* Infrastructure Monitoring Panel */}
      {dashboard?.monitoring && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold text-gray-900 text-sm">Infrastructure Monitoring</h3>
            </div>
            <div className="flex gap-2">
              <a href={dashboard.monitoring.grafana.url} target="_blank" rel="noopener noreferrer" className="text-xs px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors font-medium">Open Grafana</a>
              <a href={dashboard.monitoring.kibana.url} target="_blank" rel="noopener noreferrer" className="text-xs px-2.5 py-1 bg-violet-50 text-violet-700 rounded-lg hover:bg-violet-100 transition-colors font-medium">Open Kibana</a>
            </div>
          </div>

          {/* Service Status Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            {[
              { name: 'Prometheus', status: dashboard.monitoring.prometheus.status, desc: 'Metrics collection' },
              { name: 'Elasticsearch', status: typeof dashboard.monitoring.elasticsearch.status === 'string' ? dashboard.monitoring.elasticsearch.status : dashboard.monitoring.elasticsearch.status.status, desc: 'Log storage' },
              { name: 'Grafana', status: dashboard.monitoring.grafana.status, desc: 'Dashboards' },
              { name: 'Kibana', status: dashboard.monitoring.kibana.status, desc: 'Log search' },
            ].map(svc => (
              <div key={svc.name} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <div className={`w-2 h-2 rounded-full ${svc.status === 'healthy' || svc.status === 'green' || svc.status === 'available' ? 'bg-emerald-500' : svc.status === 'yellow' ? 'bg-yellow-500' : svc.status === 'down' ? 'bg-red-500' : 'bg-gray-400'}`} />
                <div>
                  <div className="text-xs font-medium text-gray-800">{svc.name}</div>
                  <div className="text-[10px] text-gray-500">{svc.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Live Metrics Row */}
          {dashboard.monitoring.prometheus.metrics && (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {[
                { label: 'Req/sec', value: dashboard.monitoring.prometheus.metrics.requests_per_sec?.toFixed(1) || '0', warn: false },
                { label: '5xx/sec', value: dashboard.monitoring.prometheus.metrics.error_rate_5xx?.toFixed(2) || '0', warn: (dashboard.monitoring.prometheus.metrics.error_rate_5xx || 0) > 0 },
                { label: 'p95 Latency', value: `${((dashboard.monitoring.prometheus.metrics.p95_latency || 0) * 1000).toFixed(0)}ms`, warn: (dashboard.monitoring.prometheus.metrics.p95_latency || 0) > 2 },
                { label: 'Memory', value: `${(dashboard.monitoring.prometheus.metrics.memory_mb || 0).toFixed(0)}MB`, warn: (dashboard.monitoring.prometheus.metrics.memory_mb || 0) > 400 },
                { label: 'In Flight', value: String(Math.round(dashboard.monitoring.prometheus.metrics.in_flight || 0)), warn: (dashboard.monitoring.prometheus.metrics.in_flight || 0) > 50 },
                { label: 'DB Conns', value: String(Math.round(dashboard.monitoring.prometheus.metrics.db_connections || 0)), warn: (dashboard.monitoring.prometheus.metrics.db_connections || 0) > 30 },
              ].map(m => (
                <div key={m.label} className={`text-center py-2 px-1 rounded-lg ${m.warn ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-bold ${m.warn ? 'text-red-700' : 'text-gray-900'}`}>{m.value}</div>
                  <div className="text-[10px] text-gray-500">{m.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* ES Log Stats */}
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span>Logs today: <strong className="text-gray-700">{(dashboard.monitoring.elasticsearch.logs_today || 0).toLocaleString()}</strong></span>
            <span>Errors (1hr): <strong className={`${(dashboard.monitoring.elasticsearch.errors_last_hour || 0) > 10 ? 'text-red-600' : 'text-gray-700'}`}>{dashboard.monitoring.elasticsearch.errors_last_hour || 0}</strong></span>
            {dashboard.monitoring.elasticsearch.errors_by_service && dashboard.monitoring.elasticsearch.errors_by_service.length > 0 && (
              <span className="text-gray-400">|</span>
            )}
            {dashboard.monitoring.elasticsearch.errors_by_service?.map(s => (
              <span key={s.service} className="text-gray-500">{s.service}: <strong className="text-gray-700">{s.count}</strong></span>
            ))}
          </div>

          {/* Error Breakdown */}
          {dashboard.monitoring.elasticsearch.error_breakdown && dashboard.monitoring.elasticsearch.error_breakdown.length > 0 && (
            <div className="mt-3 border-t border-gray-100 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                <span className="text-xs font-semibold text-gray-700">Error Breakdown (last 1hr)</span>
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {dashboard.monitoring.elasticsearch.error_breakdown.map((err, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span className="shrink-0 bg-red-100 text-red-700 font-bold rounded px-1.5 py-0.5 min-w-[32px] text-center">{err.count}</span>
                    <span className="text-gray-600 break-all leading-relaxed">{err.message.length > 150 ? err.message.substring(0, 150) + '...' : err.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active Monitoring Banner */}
      {dashboard?.active_monitoring && dashboard.active_monitoring.length > 0 && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-5 h-5 text-cyan-600 animate-pulse" />
            <h3 className="font-medium text-cyan-900">Post-Fix Health Monitoring Active</h3>
          </div>
          <p className="text-xs text-cyan-600 mb-2">The agent is watching system health after applying a fix. If error rates increase or the system becomes unhealthy, it will automatically revert the changes.</p>
          {dashboard.active_monitoring.map(m => (
            <div key={m.incident_id} className="text-sm text-cyan-700 bg-cyan-100/50 rounded px-3 py-1.5 mt-1">
              Incident {m.incident_id.substring(0, 8)}... — Health check #{m.check_count} — Auto-confirms in {Math.round(m.remaining_ms / 1000)}s if stable
            </div>
          ))}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
        {[
          { key: 'active', label: 'Active', tooltip: 'Incidents being detected, diagnosed, or fixed right now' },
          { key: 'fix_ready', label: 'Awaiting Approval', tooltip: 'The AI has generated a fix and is waiting for you to approve or reject it' },
          { key: 'resolved', label: 'Resolved', tooltip: 'Incidents that were successfully fixed' },
          { key: 'reverted,escalated', label: 'Failed/Escalated', tooltip: 'Fixes that were reverted or need manual investigation' },
          { key: 'all', label: 'All', tooltip: 'Show all incidents regardless of status' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            title={tab.tooltip}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${statusFilter === tab.key ? 'bg-violet-100 text-violet-700 font-medium' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Incidents List */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : incidents.length === 0 ? (
        <div className="text-center py-12">
          <Bot className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No incidents found</p>
          {dashboard?.status === 'running' ? (
            <p className="text-sm text-gray-400 mt-1">The agent is monitoring your system. Incidents will appear here when errors are detected.</p>
          ) : (
            <p className="text-sm text-gray-400 mt-1">Start the agent to begin monitoring, or use "Report an Issue" to manually submit a problem.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {incidents.map(incident => (
            <IncidentCard
              key={incident.id}
              incident={incident}
              isSelected={selectedIncident?.id === incident.id}
              onSelect={() => setSelectedIncident(selectedIncident?.id === incident.id ? null : incident)}
              onApprove={handleApproveFix}
              onReject={handleRejectFix}
              onDismiss={handleDismiss}
              onRediagnose={handleRediagnose}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Sub-Components
// ============================================================================

function StatCard({ label, value, icon: Icon, color, highlight, tooltip }: { label: string; value: string | number; icon: React.ElementType; color: string; highlight?: boolean; tooltip?: string }) {
  const colorMap: Record<string, string> = {
    green: 'bg-green-50 text-green-600 border-green-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
    red: 'bg-red-50 text-red-600 border-red-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    gray: 'bg-gray-50 text-gray-500 border-gray-100',
  };
  return (
    <div className={`rounded-xl border p-4 ${highlight ? 'ring-2 ring-orange-400 ring-offset-1' : ''} ${colorMap[color] || colorMap.gray}`} title={tooltip}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium opacity-70">{label}</span>
        <Icon className="w-4 h-4 opacity-50" />
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

function IncidentCard({ incident, isSelected, onSelect, onApprove, onReject, onDismiss, onRediagnose }: {
  incident: Incident;
  isSelected: boolean;
  onSelect: () => void;
  onApprove: (incidentId: string, fixId: string) => void;
  onReject: (incidentId: string) => void;
  onDismiss: (incidentId: string) => void;
  onRediagnose: (incidentId: string) => void;
}) {
  const config = statusConfig[incident.status] || statusConfig.detected;
  const StatusIcon = config.icon;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header Row */}
      <div className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={onSelect}>
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
          <StatusIcon className="w-3.5 h-3.5" /> {config.label}
        </div>
        <div className={`px-2 py-0.5 rounded text-xs font-medium border ${severityColor[incident.error.severity] || severityColor.medium}`}>
          {incident.error.severity.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{incident.error.message}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {incident.error.file_path && <span className="font-mono">{incident.error.file_path}:{incident.error.line_number}</span>}
            {incident.error.endpoint && <span> &middot; {incident.error.endpoint}</span>}
            {' '}&middot; {incident.error.error_count}x &middot; {timeAgo(incident.created_at)}
          </p>
        </div>
        {incident.diagnosis && (
          <div className="text-xs text-gray-400 flex items-center gap-1">
            <Zap className="w-3.5 h-3.5" /> {incident.diagnosis.confidence}% confidence
          </div>
        )}
        {isSelected ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </div>

      {/* Expanded Detail */}
      {isSelected && (
        <div className="border-t border-gray-100">
          {/* Diagnosis */}
          {incident.diagnosis && (
            <div className="p-4 bg-indigo-50/50 border-b border-gray-100">
              {/* User Error Banner */}
              {incident.diagnosis.is_user_error && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2 text-amber-800 font-medium text-sm mb-1">
                    <MessageSquare className="w-4 h-4" /> This is a User Error — Not a System Bug
                  </div>
                  <p className="text-xs text-amber-700">The user triggered expected validation or business rules. No code fix needed — they need guidance on the correct workflow.</p>
                  {incident.diagnosis.user_guidance && (
                    <p className="text-xs text-amber-900 mt-1 font-medium">Guidance: {incident.diagnosis.user_guidance}</p>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-sm font-semibold text-indigo-900 flex items-center gap-2"><Eye className="w-4 h-4" /> Root Cause</h4>
                {incident.diagnosis.error_domain && (
                  <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded font-medium">Domain: {incident.diagnosis.error_domain}</span>
                )}
              </div>
              <p className="text-sm text-indigo-800">{incident.diagnosis.root_cause}</p>
              {incident.diagnosis.explanation !== incident.diagnosis.root_cause && (
                <p className="text-xs text-indigo-600 mt-2">{incident.diagnosis.explanation}</p>
              )}

              {/* Admin Action */}
              {incident.diagnosis.admin_action && (
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-2">
                  <p className="text-xs text-blue-800"><strong>Admin Action Needed:</strong> {incident.diagnosis.admin_action}</p>
                </div>
              )}

              {/* Database Checks */}
              {incident.diagnosis.database_checks && incident.diagnosis.database_checks.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-1">Diagnostic queries:</p>
                  {incident.diagnosis.database_checks.map((q, i) => (
                    <code key={i} className="block text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded mt-1 font-mono">{q}</code>
                  ))}
                </div>
              )}

              <div className="flex gap-2 mt-3 flex-wrap">
                {incident.diagnosis.is_user_error && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-medium">User Error</span>}
                {incident.diagnosis.impact_assessment.user_facing && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">User-Facing</span>}
                {incident.diagnosis.impact_assessment.data_integrity && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Data Risk</span>}
                {incident.diagnosis.impact_assessment.security && <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">Security</span>}
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Scope: {incident.diagnosis.impact_assessment.scope}</span>
              </div>
            </div>
          )}

          {/* Fix Options */}
          {incident.fixes.length > 0 && (
            <div className="p-4 border-b border-gray-100">
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><Zap className="w-4 h-4" /> Fix Options</h4>
              <div className="space-y-3">
                {incident.fixes.map(fix => (
                  <div key={fix.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-900">{fix.title}</span>
                          <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">{fix.type}</span>
                          <span className={`text-xs font-medium ${riskColor[fix.risk_level]}`}>Risk: {fix.risk_level}</span>
                          <span className="text-xs text-gray-500">{fix.confidence}% confidence</span>
                        </div>
                        <p className="text-xs text-gray-600">{fix.description}</p>

                        {/* Patches with Diff Viewer */}
                        {fix.patches && fix.patches.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {fix.patches.map((p, i) => (
                              <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-xs text-gray-600 border-b border-gray-200">
                                  <FileCode className="w-3.5 h-3.5" />
                                  <span className="font-mono font-medium">{p.file_path}</span>
                                  <span className="text-gray-400 ml-2">{p.diff_summary}</span>
                                </div>
                                {p.unified_diff ? (
                                  <pre className="text-[11px] font-mono overflow-x-auto max-h-64 overflow-y-auto p-0 m-0">
                                    {p.unified_diff.split('\n').slice(2).map((line, li) => {
                                      const bg = line.startsWith('+') && !line.startsWith('+++') ? 'bg-green-50 text-green-800'
                                        : line.startsWith('-') && !line.startsWith('---') ? 'bg-red-50 text-red-800'
                                        : line.startsWith('@@') ? 'bg-blue-50 text-blue-700'
                                        : 'text-gray-600';
                                      return <div key={li} className={`px-3 py-0 leading-5 ${bg}`}>{line || ' '}</div>;
                                    })}
                                  </pre>
                                ) : (
                                  <div className="px-3 py-2 text-xs text-gray-400 italic">Diff not available — fix was generated before diff viewer was enabled</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Commands */}
                        {fix.commands && fix.commands.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {fix.commands.map((c, i) => (
                              <div key={i} className="text-xs flex items-center gap-1.5 text-gray-500">
                                <Terminal className="w-3.5 h-3.5" />
                                <code className="bg-gray-200 px-1 rounded">{c.command}</code>
                              </div>
                            ))}
                          </div>
                        )}

                        {fix.risk_explanation && (
                          <p className="text-xs text-amber-600 mt-1">{fix.risk_explanation}</p>
                        )}
                      </div>

                      {/* Approve Button */}
                      {incident.status === 'fix_ready' && (
                        <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); onApprove(incident.id, fix.id); }}
                            className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 flex items-center gap-1"
                            title="Apply this fix. A snapshot will be taken first so the system can be rolled back if the fix causes issues."
                          >
                            <ThumbsUp className="w-3.5 h-3.5" /> Approve & Apply
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Execution Status */}
          {incident.execution && (
            <div className="p-4 bg-gray-50 border-b border-gray-100">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">Execution</h4>
              <div className="text-xs text-gray-600">
                Status: <span className="font-medium">{incident.execution.status}</span>
                {incident.execution.error_message && (
                  <span className="text-red-600 ml-2">Error: {incident.execution.error_message}</span>
                )}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Timeline</h4>
            <div className="space-y-2">
              {incident.timeline.slice().reverse().map((event, i) => (
                <div key={i} className="flex items-start gap-3 text-xs">
                  <span className="text-gray-400 w-16 flex-shrink-0">{timeAgo(event.timestamp)}</span>
                  <span className="text-gray-400">{event.actor || 'system'}</span>
                  <span className="text-gray-700">{event.description}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-4 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center gap-2 flex-wrap">
              {incident.status === 'fix_ready' && (
                <button onClick={() => onReject(incident.id)} className="px-3 py-1.5 text-xs bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-1" title="Reject all proposed fixes. The incident will be escalated for manual investigation.">
                  <ThumbsDown className="w-3.5 h-3.5" /> Reject All Fixes
                </button>
              )}
              {!['resolved', 'dismissed', 'applying', 'reverting'].includes(incident.status) && (
                <>
                  <button onClick={() => onRediagnose(incident.id)} className="px-3 py-1.5 text-xs bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-1" title="Run a fresh AI diagnosis. Useful if conditions have changed or the first diagnosis was inaccurate.">
                    <RefreshCw className="w-3.5 h-3.5" /> Re-diagnose
                  </button>
                  <button onClick={() => onDismiss(incident.id)} className="px-3 py-1.5 text-xs bg-white border border-gray-200 text-gray-400 rounded-lg hover:bg-gray-50 flex items-center gap-1" title="Mark this as a non-issue and stop tracking it. The error will be cleared from the collector.">
                    <Trash2 className="w-3.5 h-3.5" /> Dismiss
                  </button>
                </>
              )}
            </div>
            {incident.status === 'fix_ready' && (
              <p className="text-xs text-gray-400 mt-2">Review the fix options above. Click "Approve" on the fix you want to apply. The system will take a snapshot first, then apply the fix and monitor for 5 minutes.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TroubleshootAgent;
